/**
 * Single source of truth for whether on-device (client-side) ASR should be used for a
 * session (TT-825). Consolidates the enablement conditions so the decision is not
 * scattered across the asset-cache, wiring, and benchmark layers.
 *
 * Conditions:
 * 1. Global feature flag (`ENABLE_LOCAL_ASR`) — owned by this ticket.
 * 2. District/school license — placeholder pending a dedicated license field on the
 *    Student Record Store `getAmiraLicenses` schema and client plumbing (separate
 *    ticket / backend dependency). Defaults to enabled so it is a no-op until added.
 * 3. SharedArrayBuffer availability — ORT requires multi-threaded WASM via
 *    SharedArrayBuffer to meet the 200ms stride budget. The app's COOP/COEP headers
 *    gate this: environments that serve the correct headers expose SharedArrayBuffer
 *    (Chromebooks, desktop Chrome/Edge), while environments that don't (iPads/Safari,
 *    iOS Chrome/WebKit) will not have it available. This single check replaces the
 *    previous browser/OS/platform allow-list.
 * 4. WASM SIMD — mandatory for both backends, so a device without it uses server
 *    Kaldi rather than a degraded local path. ORT's shipped wasm is SIMD-only and
 *    `assertRuntimeReady()` throws outright on `!checks.simd`, so before this gate a
 *    non-SIMD device passed enablement and then died at init; the vosk-v3 runtime and
 *    the per-phrase HCLG decode path are SIMD-only too. Checking here turns a thrown
 *    error mid-init into a fallback decision at session start.
 *
 *    Kept SEPARATE from condition 3 rather than folded into it: a device missing SIMD
 *    almost certainly also lacks SharedArrayBuffer, but the two have completely
 *    different diagnoses — "browser too old" vs "COOP/COEP headers not being served"
 *    — and this module exists so that distinction reaches the logs.
 * 5. Tutor-only session — assessments keep the server pipeline (see
 *    `setLocalAsrAssessmentSession`).
 *
 * The base `isLocalAsrEnabled()` session gate intentionally omits locale; the
 * placement-specific helper applies the en-us gate at the wiring layer (TT-828).
 */

import { FEATURE_FLAGS } from '../../constants/featureFlags';
import { getUserData, getRawTutorLocale } from '../API';
import { wasmSimdSupported } from './engine/runtime_checks';
import { globalContext } from '../../components/audiocapture/audioContext';

const EN_US_LOCALE = 'en-us';
const MIN_SAMPLE_RATE_FOR_LOCAL_ASR = 16000;

/**
 * Normalizes locale strings from common app/backend formats (`EN_US`, `en_US`,
 * `en-us`) so local placement eligibility cannot accidentally include Spanish.
 * @param {string} locale
 * @returns {string}
 */
function normalizeLocale(locale) {
  return String(locale || '').trim().toLowerCase().replace('_', '-');
}

/**
 * Whether the current environment supports on-device ASR. Checks for SharedArrayBuffer
 * availability, which requires cross-origin isolation (COOP + COEP headers). ORT needs
 * multi-threaded WASM via SharedArrayBuffer to meet the 200ms stride budget; without it
 * inference runs single-threaded at ~3-4x the latency.
 *
 * This naturally excludes iPads/iOS (WebKit never exposes SharedArrayBuffer regardless
 * of headers) and includes Chromebooks, desktop Chrome, Edge, and any other browser
 * where the app's COOP/COEP headers successfully enable SharedArrayBuffer.
 * @returns {boolean}
 */
export function isSupportedDeviceForLocalAsr() {
  return typeof SharedArrayBuffer !== 'undefined';
}

/**
 * Whether the device has WASM SIMD, which both backends require. A separate gate from
 * isSupportedDeviceForLocalAsr() on purpose — see condition 4 in the module header:
 * the two nearly always fail together, but "browser too old" and "COOP/COEP headers
 * missing" are different problems with different fixes, and collapsing them would
 * make one indistinguishable from the other in the logs.
 *
 * Delegates to the canonical probe rather than re-deriving it, so this cannot drift
 * from the ORT preflight (`assertRuntimeReady`) or the Vosk runtime-dir resolver.
 * @returns {boolean}
 */
export function isSimdSupportedForLocalAsr() {
  return wasmSimdSupported();
}

/**
 * Whether the AudioContext sample rate is viable for on-device ASR.
 *
 * Sub-16k sources (e.g. 8 kHz Bluetooth HFP/CVSD) are hard-failed by the
 * wav-worker (returns null buffer → "I can't hear you" modal). This gate
 * provides a clear `blockedBy: 'lowSampleRate'` label in the enablement log
 * so those sessions are diagnosable without digging into audio-level telemetry.
 *
 * The >= 16000 threshold allows 16 kHz HFP/mSBC (genuine pass-through, works
 * fine) and blocks only sub-16k rates. Unknown/unavailable rates fail open
 * (return true) so local ASR is not silently disabled when Web Audio is absent.
 *
 * Reads directly from the globalContext singleton (audioContext.js leaf module),
 * so the rate is available synchronously at first evaluation — no push/setter
 * needed, and the enablement log is correct from the start.
 *
 * TT-1269: Bluetooth HFP forces AudioContext to 8/16 kHz.
 * @returns {boolean}
 */
export function isAudioContextViableForLocalAsr() {
  const rate = globalContext?.sampleRate;
  if (!Number.isFinite(rate) || rate <= 0) return true;
  return rate >= MIN_SAMPLE_RATE_FOR_LOCAL_ASR;
}

/**
 * Debug URL-param override for the Browser ASR license check. Read once at
 * module load; `?forceBrowserAsr=true` bypasses the license gate so QA can
 * test without a licensed district. Set via the Debug Menu (DEV/STAGE only).
 */
const _forceBrowserAsrOverride = (() => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const value = new URLSearchParams(window.location.search).get('forceBrowserAsr');
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
})();

/**
 * Whether the current district/school is licensed for on-device ASR.
 * forceBrowserASROff is a hard override that disables browser ASR; it can appear
 * on the district license or on appSettings, and either source being true wins.
 * The district-level form exists so one district can be opted out while the
 * global appSettings enableBrowserASR rollout stays on (CR-10679).
 * enableBrowserASR can appear on either the district license or the appSettings;
 * either source being true satisfies the license requirement.
 *
 * The `?forceBrowserAsr=true` URL param bypasses this check entirely for
 * debug-menu / QA use — including both forceBrowserASROff kill switches. That
 * precedence is deliberate (QA must be able to test on an opted-out district),
 * but it means the param re-enables browser ASR on hardware a district was
 * opted out to protect; launch URLs never carry it organically.
 * @returns {boolean}
 */
export function isLocalAsrLicensed() {
  if (_forceBrowserAsrOverride !== undefined) {
    return _forceBrowserAsrOverride;
  }
  const userData = getUserData();
  if (userData.forceBrowserASROff === true ||
    userData.appSettings?.forceBrowserASROff === true) {
    return false;
  }
  return userData.enableBrowserASR === true ||
    userData.appSettings?.enableBrowserASR === true;
}

/**
 * Whether this session launches an assessment, as a three-state value: `undefined`
 * until classified, then `true`/`false`. Local ASR is tutor-only for now, so the
 * answer has to exist before the model is downloaded, not just before it is used.
 */
let _assessmentSession;

/**
 * Classifies the session as assessment or tutor for local ASR purposes.
 *
 * Callers resolve this as early as they can: `App` reads the `assignmentType` URL
 * param in its constructor for launcher entries (Amira Direct and friends), and
 * falls back to the assignment returned by `checkAssignments` for entries without
 * that param. `getUserData().assessmentMode` is deliberately not used here — it is
 * activity-scoped and only set once a reader activity starts, which is far too late
 * to stop the asset download or the runtime init.
 *
 * Resolution is sticky once true: a session that has launched an assessment keeps
 * local ASR off even if it later moves into tutor content, since re-enabling would
 * mean downloading the model mid-session. `false` may still be upgraded to `true`,
 * so an optimistic early guess costs at most a wasted partial download.
 *
 * @param {boolean} isAssessment
 * @returns {void}
 */
export function setLocalAsrAssessmentSession(isAssessment) {
  if (_assessmentSession === true) {
    return;
  }
  _assessmentSession = Boolean(isAssessment);
}

/**
 * Whether this session has been classified as an assessment.
 * @returns {boolean}
 */
export function isLocalAsrAssessmentSession() {
  return _assessmentSession === true;
}

/**
 * Whether the session has been classified either way yet. Callers that would
 * otherwise act on an unknown session (notably the asset preload) wait on this.
 * @returns {boolean}
 */
export function isLocalAsrSessionModeResolved() {
  return _assessmentSession !== undefined;
}

/**
 * Every gate isLocalAsrEnabled() applies, evaluated individually, plus the first one
 * that blocked. Exists because the enablement decision used to be logged nowhere: a
 * session that fell back to server ASR looked identical in the logs whether the flag
 * was off, the district was unlicensed, the device lacked SharedArrayBuffer or WASM
 * SIMD, or the session was classified as an assessment. `blockedBy` is null when
 * enabled.
 *
 * Evaluating all gates eagerly (rather than short-circuiting) is safe — each is a pure
 * read of a flag, module state, the user record, or a global (the SIMD probe is
 * memoized in runtime_checks for exactly this reason) — and lets one log line report
 * every gate rather than only the first failure.
 * @returns {{enabled: boolean, blockedBy: string|null, flagEnabled: boolean,
 *   assessmentSession: boolean, licensed: boolean, supportedDevice: boolean,
 *   simdSupported: boolean, viableSampleRate: boolean}}
 */
export function explainLocalAsrEnablement() {
  const gates = {
    flagEnabled: Boolean(FEATURE_FLAGS.ENABLE_LOCAL_ASR),
    assessmentSession: isLocalAsrAssessmentSession(),
    licensed: isLocalAsrLicensed(),
    supportedDevice: isSupportedDeviceForLocalAsr(),
    simdSupported: isSimdSupportedForLocalAsr(),
    viableSampleRate: isAudioContextViableForLocalAsr(),
  };

  // Order is load-bearing: `blockedBy` reports the FIRST match, and a device without
  // SIMD nearly always lacks SharedArrayBuffer too. Keeping noWasmSimd after
  // unsupportedDevice means sessions that already reported unsupportedDevice keep
  // reporting it, so existing log/dashboard queries on that value don't silently shift
  // to a new label. Both booleans are still reported independently above.
  // lowSampleRate is last because it's rare and the above gates will have already
  // blocked most ineligible sessions before this check runs.
  const blockedBy = [
    !gates.flagEnabled && 'flagDisabled',
    gates.assessmentSession && 'assessmentSession',
    !gates.licensed && 'unlicensed',
    !gates.supportedDevice && 'unsupportedDevice',
    !gates.simdSupported && 'noWasmSimd',
    !gates.viableSampleRate && 'lowSampleRate',
  ].find(Boolean) || null;

  return { ...gates, blockedBy, enabled: blockedBy === null };
}

/**
 * Whether on-device ASR should be used for this session: feature flag AND a tutor
 * (non-assessment) session AND supported device AND WASM SIMD AND district license.
 * The locale gate (en-us) is applied separately at the wiring layer (TT-828).
 *
 * The assessment check lives here rather than only in `shouldUseLocalPlacementAsr()`
 * because the download (`App.preloadLocalAsrAssets`) and the runtime init
 * (`SpeechStream.initLocalPlacementAsr`) both gate on this function and neither
 * consults the placement helper.
 *
 * Delegates to explainLocalAsrEnablement() so the logged reason can never disagree
 * with the decision actually taken.
 * @returns {boolean}
 */
export function isLocalAsrEnabled() {
  return explainLocalAsrEnablement().enabled;
}

/**
 * Resolves the locale local ASR phrase routing should gate on (TT-1258).
 *
 * Precedence:
 *   1. storyLocale — the language the student is actually reading (from story tags).
 *      Most authoritative because it reflects the content being decoded.
 *   2. rawTutorLocale — the `?tutorLocale=` URL param, indicating which tutor was
 *      launched. Useful before a story is loaded (e.g. asset download decisions).
 *   3. userData.locale — session/UI locale. Last resort; for bilingual students this
 *      is `es-mx` even when reading English, which is the mismatch this resolver fixes.
 *
 * @param {object} [options]
 * @param {string} [options.storyLocale] Content locale from getStoryContentLocale(story).
 * @returns {string} Normalized locale string (e.g. 'en-us', 'es-mx').
 */
export function resolveLocalAsrPlacementLocale({ storyLocale } = {}) {
  if (storyLocale) return normalizeLocale(storyLocale);
  const rawTutorLocale = getRawTutorLocale();
  if (rawTutorLocale) return normalizeLocale(rawTutorLocale);
  return normalizeLocale(getUserData().locale);
}

/**
 * Whether the current locale/session may use local ASR for placement. This app only
 * calls local placement ASR from standard phrase-reading flows with audio recording
 * enabled; other ASR modes are excluded by their existing call-site routing guards.
 *
 * The session-level assessment gate is applied by isLocalAsrEnabled(). The extra
 * `assessmentMode` check here is an independent, activity-scoped backstop: it is set
 * by setSessionActivityId when a reader activity starts, so it also catches an
 * assessment activity inside a session that was classified as tutor.
 * @param {object} options
 * @param {string} options.locale Current session/story locale.
 * @returns {boolean}
 */
export function shouldUseLocalPlacementAsr({ locale } = {}) {
  if (getUserData().assessmentMode) {
    return false;
  }
  return isLocalAsrEnabled() && normalizeLocale(locale) === EN_US_LOCALE;
}
